import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WalletConnectionPanel = ({ onConnectionChange }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [selectedWallet, setSelectedWallet] = useState('');

  const walletProviders = [
    {
      id: 'metamask',
      name: 'MetaMask',
      icon: 'Wallet',
      description: 'Connect using MetaMask browser extension',
      isInstalled: true,
      isRecommended: true
    },
    {
      id: 'walletconnect',
      name: 'WalletConnect',
      icon: 'Smartphone',
      description: 'Connect using mobile wallet',
      isInstalled: true,
      isRecommended: false
    },
    {
      id: 'coinbase',
      name: 'Coinbase Wallet',
      icon: 'CreditCard',
      description: 'Connect using Coinbase Wallet',
      isInstalled: false,
      isRecommended: false
    },
    {
      id: 'trust',
      name: 'Trust Wallet',
      icon: 'Shield',
      description: 'Connect using Trust Wallet',
      isInstalled: false,
      isRecommended: false
    }
  ];

  const handleWalletConnect = async (walletId) => {
    setIsConnecting(true);
    setConnectionError('');
    setSelectedWallet(walletId);

    try {
      // Simulate wallet connection process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate successful connection
      const mockWalletData = {
        address: '0x742d35Cc6634C0532925a3b8D4C9db96590b5b8c',
        balance: '2.45',
        network: 'Polygon',
        provider: walletId
      };

      onConnectionChange(mockWalletData);
    } catch (error) {
      setConnectionError('Failed to connect wallet. Please try again.');
    } finally {
      setIsConnecting(false);
      setSelectedWallet('');
    }
  };

  const handleInstallWallet = (walletId) => {
    const installUrls = {
      metamask: 'https://metamask.io/download/',
      coinbase: 'https://wallet.coinbase.com/',
      trust: 'https://trustwallet.com/download'
    };

    if (installUrls?.[walletId]) {
      window.open(installUrls?.[walletId], '_blank');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
          <Icon name="Wallet" size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-semibold text-foreground mb-2">Connect Your Wallet</h2>
        <p className="text-muted-foreground">
          Choose a wallet to connect and start notarizing webpages on the blockchain
        </p>
      </div>
      {connectionError && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <p className="text-sm text-destructive font-medium">{connectionError}</p>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {walletProviders?.map((wallet) => (
          <div
            key={wallet?.id}
            className={`relative p-4 border rounded-lg transition-smooth ${
              wallet?.isInstalled 
                ? 'border-border hover:border-primary/50 hover:bg-primary/5 cursor-pointer' :'border-muted bg-muted/30'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${
                  wallet?.isInstalled ? 'bg-primary/10' : 'bg-muted'
                }`}>
                  <Icon 
                    name={wallet?.icon} 
                    size={24} 
                    className={wallet?.isInstalled ? 'text-primary' : 'text-muted-foreground'} 
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className={`font-medium ${
                      wallet?.isInstalled ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {wallet?.name}
                    </h3>
                    {wallet?.isRecommended && (
                      <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${
                    wallet?.isInstalled ? 'text-muted-foreground' : 'text-muted-foreground/70'
                  }`}>
                    {wallet?.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {wallet?.isInstalled ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleWalletConnect(wallet?.id)}
                    loading={isConnecting && selectedWallet === wallet?.id}
                    disabled={isConnecting}
                  >
                    {isConnecting && selectedWallet === wallet?.id ? 'Connecting...' : 'Connect'}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleInstallWallet(wallet?.id)}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Install
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">New to Web3 wallets?</p>
            <p className="text-sm text-muted-foreground mt-1">
              We recommend MetaMask for beginners. It's secure, easy to use, and works great with PolyNotary.
            </p>
            <Button
              variant="link"
              size="sm"
              className="p-0 h-auto mt-2 text-primary"
              onClick={() => window.open('https://metamask.io/faqs/', '_blank')}
            >
              Learn more about MetaMask →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletConnectionPanel;